import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getTeamAvatarUrl, handleTeamAvatarError } from '../utils/avatars';
import Pagination from './Pagination';
import CountryDisplay from './CountryDisplay';
import TeamMembersModal from './TeamMembersModal';
import './TeamsList.css';

// Компонент для отображения описания с tooltip
const DescriptionCell = ({ description }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const textRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      // Проверяем, обрезан ли текст
      setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [description]);

  const handleMouseEnter = () => {
    if (!isOverflowing || !wrapperRef.current) return;
    
    const rect = wrapperRef.current.getBoundingClientRect();
    
    // Создаем временный элемент для измерения размеров tooltip
    const tempTooltip = document.createElement('div');
    tempTooltip.className = 'description-tooltip';
    tempTooltip.style.visibility = 'hidden';
    tempTooltip.style.position = 'fixed';
    tempTooltip.style.top = '-9999px';
    tempTooltip.textContent = description;
    document.body.appendChild(tempTooltip);
    
    const tooltipWidth = tempTooltip.offsetWidth;
    const tooltipHeight = tempTooltip.offsetHeight;
    document.body.removeChild(tempTooltip);
    
    // Позиционируем tooltip над ячейкой
    setTooltipPosition({
      top: rect.top - tooltipHeight - 10,
      left: Math.max(10, rect.left + rect.width / 2 - tooltipWidth / 2)
    });
    
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  if (!description) return <div className="team-description-cell">-</div>;

  return (
    <>
      <div 
        ref={wrapperRef}
        className="team-description-cell-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div ref={textRef} className="team-description-cell">
          {description}
        </div>
      </div>
      {showTooltip && (
        <div 
          className="description-tooltip"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`
          }}
        >
          {description}
        </div>
      )}
    </>
  );
};

const TeamsList = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20
  });
  const [error, setError] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [activeTooltip, setActiveTooltip] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, searchQuery]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
      const response = await axios.get(`/api/teams?page=${pagination.current_page}${searchParam}`);
      setTeams(response.data.teams);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = () => {
    navigate('/teams/new');
  };

  const handleEditTeam = (teamId) => {
    navigate(`/teams/${teamId}/edit`);
  };

  const handleApproveTeam = async (teamId) => {
    if (!window.confirm(t('teams.confirmApprove'))) return;

    try {
      await axios.post(`/api/teams/${teamId}/approve`);
      fetchTeams();
    } catch (err) {
      alert(t('teams.approveError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSuspendTeam = async (teamId) => {
    if (!window.confirm(t('teams.confirmSuspend'))) return;

    try {
      await axios.post(`/api/teams/${teamId}/suspend`);
      fetchTeams();
    } catch (err) {
      alert(t('teams.suspendError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleActivateTeam = async (teamId) => {
    if (!window.confirm(t('teams.confirmActivate'))) return;

    try {
      await axios.post(`/api/teams/${teamId}/activate`);
      fetchTeams();
    } catch (err) {
      alert(t('teams.activateError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active':
        return 'team-status-active';
      case 'pending':
        return 'team-status-pending';
      case 'suspended':
        return 'team-status-suspended';
      default:
        return '';
    }
  };

  const getStatusText = (status) => {
    return t(`teams.status.${status}`);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current_page: newPage }));
  };

  const handleShowMembers = (team) => {
    setSelectedTeam(team);
  };

  const handleCloseModal = () => {
    setSelectedTeam(null);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setSearchQuery(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleMouseEnter = (e, tooltipId) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    
    const tempTooltip = document.createElement('div');
    tempTooltip.className = 'tooltip-global';
    tempTooltip.style.visibility = 'hidden';
    tempTooltip.style.position = 'fixed';
    tempTooltip.style.top = '-9999px';
    
    let tooltipText = '';
    if (tooltipId.startsWith('edit-')) tooltipText = t('common.edit');
    if (tooltipId.startsWith('approve-')) tooltipText = t('teams.approve');
    if (tooltipId.startsWith('suspend-')) tooltipText = t('teams.suspend');
    if (tooltipId.startsWith('activate-')) tooltipText = t('teams.activate');
    
    tempTooltip.textContent = tooltipText;
    document.body.appendChild(tempTooltip);
    const tooltipWidth = tempTooltip.offsetWidth;
    document.body.removeChild(tempTooltip);
    
    setTooltipPosition({
      top: rect.top - 40,
      left: rect.left + rect.width / 2 - tooltipWidth / 2
    });
    setActiveTooltip(tooltipId);
  };

  const handleMouseLeave = () => {
    setActiveTooltip(null);
  };

  if (loading && teams.length === 0) {
    return <div className="teams-container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (error) {
    return <div className="teams-container"><div className="error">{error}</div></div>;
  }

  return (
    <div className={`teams-container ${isMobile ? 'mobile-view' : ''}`}>
      <div className="teams-header">
        <h2>{t('teams.title')}</h2>
        <button className="create-team-btn" onClick={handleCreateTeam}>
          {t('teams.createTeam')}
        </button>
      </div>

      <div className="teams-search">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('teams.searchPlaceholder')}
            className="search-input"
          />
          <button type="submit" className="search-button">
            {t('teams.search')}
          </button>
          {searchQuery && (
            <button type="button" onClick={handleClearSearch} className="clear-search-button">
              {t('teams.clearSearch')}
            </button>
          )}
        </form>
        {searchQuery && (
          <div className="search-info">
            {t('teams.searchResults')}: "{searchQuery}"
          </div>
        )}
      </div>

      <div className="teams-stats">
        {t('teams.totalTeams')}: {pagination.total_count}
      </div>

      {teams.length === 0 ? (
        <div className="no-teams">
          {searchQuery ? t('teams.noSearchResults') : t('teams.noTeams')}
        </div>
      ) : (
        <>
          {isMobile ? (
            <div className="teams-cards-container">
              <div className="teams-cards-grid">
                {teams.map((team) => (
                  <div key={team.id} className={`team-card ${team.status === 'suspended' ? 'suspended-team' : ''}`}>
                    <div className="team-card-header">
                      <img 
                        src={getTeamAvatarUrl(team.avatar, team.name)} 
                        alt={team.name} 
                        className="team-card-avatar"
                        onError={(e) => handleTeamAvatarError(e, team.name)}
                      />
                    </div>
                    
                    <div className="team-card-body">
                      <div className="team-card-title">{team.name}</div>
                      
                      <div className="team-card-info">
                        <div className="team-card-info-row">
                          <span className="team-card-label">{t('teams.teamStatus')}:</span>
                          <span className={getStatusClass(team.status)}>
                            {getStatusText(team.status)}
                          </span>
                        </div>
                        
                        <div className="team-card-info-row">
                          <span className="team-card-label">{t('teams.country')}:</span>
                          <CountryDisplay country={team.country} language={language} />
                        </div>
                        
                        <div className="team-card-info-row">
                          <span className="team-card-label">{t('teams.roles.manager')}:</span>
                          <span>{team.admin?.nickname || '-'}</span>
                        </div>
                        {team.captain && (
                          <div className="team-card-info-row">
                            <span className="team-card-label captain-label">{t('teams.roles.captain')}:</span>
                            <span>{team.captain.nickname}</span>
                          </div>
                        )}
                        
                        <div className="team-card-info-row">
                          <span className="team-card-label">{t('teams.members')}:</span>
                          <button 
                            className="members-count-button"
                            onClick={() => handleShowMembers(team)}
                          >
                            {team.members_count}
                          </button>
                        </div>
                        
                        {team.description && (
                          <div className="team-card-description">
                            <span className="team-card-label">{t('teams.description')}:</span>
                            <p>{team.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="team-card-footer">
                      {team.can_edit && (
                        <button
                          onClick={() => handleEditTeam(team.id)}
                          className="btn-icon btn-icon-edit"
                          aria-label={t('common.edit')}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                      )}
                      {user.admin && team.status === 'pending' && (
                        <button
                          onClick={() => handleApproveTeam(team.id)}
                          className="btn-icon btn-icon-approve"
                          aria-label={t('teams.approve')}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                          </svg>
                        </button>
                      )}
                      {user.admin && team.status === 'active' && (
                        <button
                          onClick={() => handleSuspendTeam(team.id)}
                          className="btn-icon btn-icon-suspend"
                          aria-label={t('teams.suspend')}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="15" y1="9" x2="9" y2="15"/>
                            <line x1="9" y1="9" x2="15" y2="15"/>
                          </svg>
                        </button>
                      )}
                      {user.admin && team.status === 'suspended' && (
                        <button
                          onClick={() => handleActivateTeam(team.id)}
                          className="btn-icon btn-icon-activate"
                          aria-label={t('teams.activate')}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            <path d="M9 12l2 2 4-4"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="table-wrapper">
        <table className="teams-table">
          <thead>
            <tr>
              <th>{t('teams.name')}</th>
              <th>{t('teams.teamStatus')}</th>
              <th>{t('teams.country')}</th>
              <th>{t('teams.roles.manager')} / {t('teams.roles.captain')}</th>
              <th>{t('teams.members')}</th>
              <th>{t('teams.description')}</th>
              <th>{t('teams.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {teams.map((team) => (
              <tr key={team.id} className={team.status === 'suspended' ? 'suspended-team' : ''}>
                <td>
                  <div className="team-name">
                    <img 
                      src={getTeamAvatarUrl(team.avatar, team.name)} 
                      alt={team.name} 
                      className="team-avatar-small"
                      onError={(e) => handleTeamAvatarError(e, team.name)}
                    />
                    {team.name}
                  </div>
                </td>
                <td>
                  <span className={getStatusClass(team.status)}>
                    {getStatusText(team.status)}
                  </span>
                </td>
                <td>
                  <CountryDisplay country={team.country} language={language} />
                </td>
                <td>
                  <div className="admin-info">
                    <div>
                      <span className="role-label-small">{t('teams.roles.manager')}:</span>
                      {' '}{team.admin?.nickname || '-'}
                    </div>
                    {team.captain && (
                      <div>
                        <span className="role-label-small captain-label">{t('teams.roles.captain')}:</span>
                        {' '}{team.captain.nickname}
                      </div>
                    )}
                  </div>
                </td>
                <td className="team-members-cell">
                  <button 
                    className="members-count-button"
                    onClick={() => handleShowMembers(team)}
                    title={t('teams.viewMembers')}
                  >
                    {team.members_count}
                  </button>
                </td>
                <td>
                  <DescriptionCell description={team.description} />
                </td>
                <td>
                  <div className="team-actions">
                    {team.can_edit && (
                      <button
                        onClick={() => handleEditTeam(team.id)}
                        onMouseEnter={(e) => handleMouseEnter(e, `edit-${team.id}`)}
                        onMouseLeave={handleMouseLeave}
                        className="btn-icon btn-icon-edit"
                        aria-label={t('common.edit')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                    )}
                    {user.admin && team.status === 'pending' && (
                      <button
                        onClick={() => handleApproveTeam(team.id)}
                        onMouseEnter={(e) => handleMouseEnter(e, `approve-${team.id}`)}
                        onMouseLeave={handleMouseLeave}
                        className="btn-icon btn-icon-approve"
                        aria-label={t('teams.approve')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                      </button>
                    )}
                    {user.admin && team.status === 'active' && (
                      <button
                        onClick={() => handleSuspendTeam(team.id)}
                        onMouseEnter={(e) => handleMouseEnter(e, `suspend-${team.id}`)}
                        onMouseLeave={handleMouseLeave}
                        className="btn-icon btn-icon-suspend"
                        aria-label={t('teams.suspend')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="15" y1="9" x2="9" y2="15"/>
                          <line x1="9" y1="9" x2="15" y2="15"/>
                        </svg>
                      </button>
                    )}
                    {user.admin && team.status === 'suspended' && (
                      <button
                        onClick={() => handleActivateTeam(team.id)}
                        onMouseEnter={(e) => handleMouseEnter(e, `activate-${team.id}`)}
                        onMouseLeave={handleMouseLeave}
                        className="btn-icon btn-icon-activate"
                        aria-label={t('teams.activate')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                          <path d="M9 12l2 2 4-4"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
          )}
        </>
      )}

      <Pagination pagination={pagination} onPageChange={handlePageChange} t={t} />

      {selectedTeam && (
        <TeamMembersModal 
          team={selectedTeam} 
          onClose={handleCloseModal} 
        />
      )}

      {activeTooltip && (
        <div 
          className="tooltip tooltip-global"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            opacity: 1
          }}
        >
          {activeTooltip.startsWith('edit-') && t('common.edit')}
          {activeTooltip.startsWith('approve-') && t('teams.approve')}
          {activeTooltip.startsWith('suspend-') && t('teams.suspend')}
          {activeTooltip.startsWith('activate-') && t('teams.activate')}
        </div>
      )}
    </div>
  );
};

export default TeamsList;